import { Component, effect, inject, OnInit } from '@angular/core';
import { GlobalsStore } from '../../../stores/globals.state';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { Commune, PieConfiguration } from '../../../models/commune';
import { NgIf } from '@angular/common';
import { NgxSliderComponent } from '../../../ngx-slider/ngx-slider.component';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import { DividerModule } from 'primeng/divider';
import { CheckboxModule } from 'primeng/checkbox';
import { ColorsetDropdownComponent } from '../../atoms/colorset-dropdown/colorset-dropdown.component';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { HeaderComponent } from '../../atoms/header/header.component';
import { RadioButtonModule } from 'primeng/radiobutton';

@Component({
  selector: 'app-configuration-chart-pie',
  standalone: true,
  imports: [
    FormsModule,
    NgIf,
    NgxSliderComponent,
    ReactiveFormsModule,
    DropdownModule,
    FormFieldWrapperComponent,
    DividerModule,
    CheckboxModule,
    ColorsetDropdownComponent,
    HeaderComponent,
    RadioButtonModule,
  ],
  templateUrl: './configuration-chart-pie.component.html',
  styleUrl: './configuration-chart-pie.component.scss',
})
export class ConfigurationChartPieComponent implements OnInit {
  readonly communeStore = inject(CommuneStore);
  readonly globalStore = inject(GlobalsStore);
  form: FormGroup = new FormGroup({});
  initialized = false;
  items: MenuItem[] = [];
  activeCommune: Commune | null = null;
  pieConfiguration?: PieConfiguration;

  // todo: build translations in ngx-slider-component, easier to use!
  pieSliderOptions = {
    ceil: 1,
    floor: 0,
    step: 0.1,
  };

  constructor(private fb: FormBuilder) {
    effect(() => {
      this.communeSetup();
    });
  }

  /**
   * Configures and initializes the active commune's setup, including fetching and applying necessary configurations.
   *
   * @return {void} No return value.
   */
  communeSetup(): void {
    if (!this.activeCommune) {
      this.activeCommune = this.globalStore.activeCommune();
      if (!this.activeCommune) return;
      if (!this.activeCommune.atlasConfig) {
        this.activeCommune.atlasConfig = this.communeStore.baseConfig();
      }
      this.pieConfiguration = this.activeCommune.atlasConfig.pie;
    }
    if (this.activeCommune && !this.initialized) {
      this.initForm();
    }
  }

  /**
   * Lifecycle hook that initializes the component after Angular has initialized all data-bound properties.
   * This method is commonly used to perform component setup or data fetching required when the component is initialized.
   * In this implementation, it triggers the communeSetup method to configure specific component settings or perform setup logic.
   *
   * @return {void} Does not return any value.
   */
  ngOnInit(): void {
    this.communeSetup();
  }

  /**
   * Initializes the form with default or provided configuration values.
   *
   * @return {void} This method does not return a value.
   */
  initForm(): void {
    this.form = this.fb.group({
      //sort: [this.pieConfiguration?.sort || 'name'],
      sort: this.fb.group({
        enable: [this.pieConfiguration?.sort?.enable || false],
        property: [this.pieConfiguration?.sort?.property || 'label'],
        direction: [this.pieConfiguration?.sort?.direction || 'asc'],
      }),
      displayValues: [this.pieConfiguration?.displayValues || false],
      colorset: [this.pieConfiguration?.colorset],
      innerRadius: [this.pieConfiguration?.innerRadius || 0],
    });

    this.form.valueChanges.subscribe(() => {
      this.submit();
    });
    this.initialized = true;
  }

  /**
   * Submits the current form data and updates the active commune's configuration.
   *
   * This method modifies the `pie` property of the active commune's `atlasConfig`
   * by merging the current form values into the existing configuration. After applying
   * these updates, it submits the modified commune object to the store.
   *
   * @return {void} Does not return a value.
   */
  submit(): void {
    if (!this.activeCommune) return;

    this.activeCommune.atlasConfig.pie = {
      ...this.activeCommune.atlasConfig.pie,
      ...this.form.value,
    };
    this.communeStore.update(this.activeCommune);
  }
}
